<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('oneClickServicesPageTitle') }}
    </h1>
    <h2 class="text-lg md:text-xl text-brand-black-body">
      {{ $t('availableServices') }}
    </h2>
    <div class="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <template :key="key" v-for="(service, key) in gridData.descriptions">
        <ServiceCardVue
          :label="service.childTransactionDescription"
          :serviceId="service.serviceTransId"
          :can-share="false"
        >
          <template #icon>
            <i
              :class="[
                [cancelVisa, cancelResidency].includes(service.serviceTransId)
                  ? 'ica-custom-fonts-card-cancel'
                  : 'ica-custom-fonts-service-card'
              ]"
              class="text-primary text-3xl"
            ></i>
          </template>
          <template #applicationTracking>&nbsp;</template>
          <template #serviceCard>&nbsp;</template>
          <template #subtitle>
            <div class="text-xs font-sans capitalize from-brand-black-autofill">
              <span v-if="service.serviceTransId == preEntryExtendVisa">
                {{ $t('preExtendVisaTransGroup') }}
              </span>
              <span v-else>{{ service.transactionsGroup }}</span>
            </div>
          </template>
          <template #footer>
            <div class="w-full flex flex-col gap-y-2 text-xs text-white font-bold">
              <RouterLink
                :to="{
                  name: 'ServiceCardView',
                  params: {
                    category,
                    serviceId: service.serviceTransId
                  }
                }"
                class="text-xs flex-1 rounded-full px-3 py-1.5 border border-primary text-primary font-semibold text-center hover:scale-105"
              >
                {{ $t('serviceCard') }}
              </RouterLink>
              <button
                class="flex-1 rounded-xl px-1 py-2 bg-primary text-center hover:scale-105 active:scale-100"
                @click="onViewBeneficiariesClick(service)"
              >
                {{ $t('ViewBeneficiaries') }}
              </button>
            </div>
          </template>
        </ServiceCardVue>
      </template>
    </div>

    <div v-if="selectedService.showBeneficiarySection" class="w-full h-full flex flex-col gap-y-2">
      <h2
        v-if="selectedService.beneficiarySectionTitle"
        class="text-lg md:text-xl text-brand-black-body"
      >
        {{ $t(selectedService.beneficiarySectionTitle) }}
      </h2>

      <DataTable
        :default-headers="[
          'arabicName',
          'englishName',
          'unifiedNumber',
          'moduleType',
          'identityNumber',
          'fileVisaNumber',
          'fileNumber',
          'serviceStart'
        ]"
        :data-set="dataSet"
      ></DataTable>
    </div>
    <Modal :title="$t('goTo')" v-model="modal" container-class="w-[32rem]">
      <template #bodyContent>
        <p>
          {{
            $t(
              selectedService.isCancelVisa
                ? 'sponsoredOrOldSponsorOrCanceledFiles'
                : 'sponsoredOrRelativeAndFriendsVisa'
            )
          }}
        </p>
      </template>
      <template #footerContent>
        <div class="w-full flex justify-end gap-x-2">
          <button
            class="px-2 py-0.5 text-center rounded-md bg-brand-green/80 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white text-xs font-sans"
            @click="loadEligiblePersons(0)"
          >
            {{ $t('Sponsored') }}
          </button>
          <button
            v-if="!selectedService.isCancelVisa"
            class="px-2 py-0.5 text-center rounded-md bg-brand-green/80 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white text-xs font-sans"
            @click="loadEligiblePersons(2)"
          >
            {{ $t('IndividualOldSponspored') }}
          </button>
          <button
            v-if="!selectedService.isCancelVisa"
            class="px-2 py-0.5 text-center rounded-md bg-brand-green/80 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white text-xs font-sans"
            @click="loadEligiblePersons(1)"
          >
            {{ $t('CanceledFiles') }}
          </button>
          <button
            v-if="selectedService.isCancelVisa"
            class="px-2 py-0.5 text-center rounded-md bg-brand-green/80 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white text-xs font-sans"
            @click="loadEligiblePersons(5)"
          >
            {{ $t('visasForVisitingRelativesAndFriends') }}
          </button>
          <button
            class="px-2 py-0.5 text-center rounded-md bg-brand-gray-600/80 hover:bg-brand-gray-600 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white text-xs font-sans"
            @click="modal = false"
          >
            {{ $t('Close') }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { onMounted, ref, reactive, computed } from 'vue'
import axiosInstance from '@/services'
import { useLookupsStore } from '@/stores/lookups'
import client_lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import ServiceCardVue from '@/components/cards/ServiceCard.vue'
import Modal from '@/components/Ui/Modal/index.vue'
import DataTable from '@/components/DataTable/index.vue'

const route = useRoute()
const router = useRouter()

const {
  passportStatus: { Nearly, Expired },
  application: { residencyCancelled, establishment, gcc, visaHolder },
  ServiceTransactions: {
    cancelVisa,
    cancelResidency,
    preEntryExtendVisa,
    reportsResidenceDetails,
    reportsVisaDetails,
    issuePassport,
    fines
  }
} = client_lookups

const { visasHoldersAllowedOneClickServiceList } = client_config

const modal = ref(false)
const userInfo = ref()

const requiredData = reactive({
  serviceDescriptions: [],
  passportStatuses: [],
  modules: [],
  parentServices: [],
  services: null
})

const gridData = reactive({
  modules: [],
  services: [],
  descriptions: []
})

const selectedService = reactive({
  isCancelVisa: false,
  serviceTransId: null,
  service: null,
  beneficiarySectionTitle: null,
  showBeneficiarySection: false,
  canceledSponsoredFlag: 0
})

const paginatorConfig = reactive({
  totalItemCount: 0,
  itemsPerPage: 5,
  currentPage: 1,
  pageCount: 0
})

const tableData = reactive({
  collection: []
})

const lookupsStore = useLookupsStore()
const authStore = useAuthStore()

const isResidencyCancelled = computed(() => authStore.user.applicationId == residencyCancelled)

const isVisaHolder = computed(() => authStore.user.applicationId == visaHolder)

const individualHasSponsorFile = computed(() => {
  const { applicationId } = authStore.user
  if (!userInfo.value || [establishment, gcc].includes(applicationId)) return
  const { sponsorFileDepartments } = userInfo.value
  return (sponsorFileDepartments ?? []).length > 0
})

const dataSet = computed(() => {
  if ((tableData.collection ?? []).length === 0) return []

  const { serviceTransId } = selectedService

  return tableData.collection.map((c) => {
    const row = {
      arabicName: c.arabicName,
      englishName: c.englishName,
      unifiedNumber: c.unifiedNumber
    }

    if ([cancelVisa, reportsVisaDetails, reportsResidenceDetails, fines].includes(serviceTransId)) {
      const moduleName = requiredData.modules.find((m) => m.id == c.moduleId)?.text
      const parentServiceName = requiredData.parentServices.find((p) => p.id == c.parentId)?.text
      const serviceName = requiredData.services.find((s) => s.id == c.serviceTranId)
      // TODO: truncate and maybe add a tooltip
      row.moduleType = [moduleName, parentServiceName, serviceName]
    } else {
      row.identityNumber = c.identityNumber
    }

    if (selectedService.serviceTransId == cancelVisa) {
      row.fileVisaNumber = c.fileNumber
    }

    if ([reportsResidenceDetails, reportsVisaDetails].includes(selectedService.serviceTransId)) {
      row.fileNumber = c.fileNumber
    }

    row.serviceStart = ''

    return row
  })
})

onMounted(async () => {
  const { isUserEstablishment, isFreezone } = authStore.user
  if (isUserEstablishment && isFreezone && route.query?.childEstablishmentId) {
    router.replace('/')
  }

  requiredData.serviceDescriptions = await axiosInstance.get('/api/service/nameDetails')

  requiredData.modules = await lookupsStore.fetchLookup('/api/lookup/modules')
  requiredData.parentServices = await lookupsStore.fetchLookup('/api/lookup/moduleParentService')
  requiredData.services = await lookupsStore.fetchLookup('/api/lookup/services')

  const passportStatuses = await lookupsStore.fetchLookup('/api/lookup/passportStatus')
  requiredData.passportStatuses = passportStatuses.filter((ps) => [Nearly, Expired].includes(ps.id))

  await loadServices()
})

async function loadServices() {
  const { applicationId } = authStore.user

  const isIndividual = [establishment, gcc].includes(applicationId) === false
  if (isIndividual) {
    userInfo.value = await axiosInstance.get('/api/individual/info')

    if (individualHasSponsorFile.value) {
      await loadIndividualServices()
    }
  }

  if (authStore.isUserEstablishment) {
    await loadEstablishmentServices()
  }
}

async function loadEstablishmentServices() {}

async function loadIndividualServices() {
  gridData.modules = await axiosInstance.get('/api/application/service/transactions')

  gridData.modules.forEach((module) => {
    module.moduleServices.forEach((parentService) => {
      parentService.childs.forEach((subService) => {
        subService.serviceTransactions.forEach((transaction) => {
          gridData.services.push(transaction.id)
        })
      })
    })
  })

  let allowedServices = [400, 406, 692, 414, 446, 447, 996, 407]
  if (route.query?.serviceTransId) {
    allowedServices = [+route.query?.serviceTransId]
  }

  let allowedTransactionIds = [0]

  if (isVisaHolder.value) {
    allowedServices = visasHoldersAllowedOneClickServiceList
  }

  gridData.descriptions = requiredData.serviceDescriptions
    .filter((sd) => {
      return gridData.services.includes(sd.serviceTransId)
    })
    .filter((sd) => {
      return allowedServices.includes(
        sd.serviceTransId || allowedTransactionIds.includes(sd.transactionId)
      )
    })
}

async function onViewBeneficiariesClick(service) {
  selectedService.showBeneficiarySection = false
  selectedService.isCancelVisa = false
  selectedService.serviceTransId = null
  selectedService.service = null
  selectedService.beneficiarySectionTitle = null

  const { serviceTransId } = service
  selectedService.serviceTransId = serviceTransId
  selectedService.service = service
  selectedService.passportStatusId = Expired

  const shouldOpenModal = [reportsResidenceDetails, reportsVisaDetails, cancelVisa].includes(
    serviceTransId
  )

  /**
   * openPopup
   */
  if (shouldOpenModal) {
    selectedService.isCancelVisa = cancelVisa == serviceTransId
    if (selectedService.isCancelVisa && authStore.isUserEstablishment) {
      /**
       * TODO:
       *  $scope.openPopup
       * web/client/shared/pages/oneClickServices/serviceList/oneClickServiceListCtrl.js
       */
    }

    modal.value = true

    return
  }

  await loadEligiblePersons(serviceTransId)
}

async function loadEligiblePersons(canceledSponsoredFlag = 0) {
  selectedService.canceledSponsoredFlag = canceledSponsoredFlag
  if (selectedService.serviceTransId == issuePassport) {
    selectedService.beneficiarySectionTitle = 'familymembers'
  } else if (
    [reportsResidenceDetails, reportsVisaDetails, cancelVisa].includes(
      selectedService.serviceTransId
    )
  ) {
    switch (selectedService.canceledSponsoredFlag) {
      case 1:
        selectedService.beneficiarySectionTitle = 'CanceledFiles'
        break
      case 2:
        selectedService.beneficiarySectionTitle = 'IndividualOldSponspored'
        break
      case 5:
        selectedService.beneficiarySectionTitle = 'visasForVisitingRelativesAndFriends'
        break

      default:
        selectedService.beneficiarySectionTitle = 'sponsored'
        break
    }
  } else {
    selectedService.beneficiarySectionTitle = 'sponsored'
  }

  await searchPersons()
}

async function searchPersons() {
  paginatorConfig.currentPage = 1

  let canceledSponsoredFlag = 0
  if (
    [reportsResidenceDetails, reportsVisaDetails, cancelVisa].includes(
      selectedService.serviceTransId
    )
  ) {
    canceledSponsoredFlag = selectedService.canceledSponsoredFlag
  }

  const body = {
    pageSize: paginatorConfig.itemsPerPage,
    currentPage: paginatorConfig.currentPage,
    serviceTransactionId: selectedService.serviceTransId,
    passportStatusId: selectedService.passportStatusId,
    canceledSponsoredFlag,
    establishmentId: null,
    unifiedNumber: null,
    identityNumber: null,
    employeeName: null
  }
  const response = await axiosInstance.post('/api/oneClickServices/eligiblePersons', body)

  paginatorConfig.totalItemCount = response.numberOfRecords ?? 0
  paginatorConfig.pageCount = Math.ceil(
    (response.numberOfRecords || 1) / paginatorConfig.itemsPerPage
  )

  tableData.collection = response.collection
  selectedService.showBeneficiarySection = true
  modal.value = false
}
</script>

<style lang="scss" scoped></style>
